import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { FicheDePrep, FDPPDFSettings } from '@teetsh/app/src/types/sequences';
import { PdfResponse } from 'types/pdf';
import useNotification from 'containers/App/Notifications/useNotification';
import { useFieldsToDisplay } from 'containers/Sequences/Preview/Sidebar/FieldsToDisplay/atom';
import { useTranslation } from 'react-i18next';
import { buildFDPPDFParams } from 'containers/Sequences/Preview/Sidebar/buildFDPPDFParams';

interface Props {
  settings?: FDPPDFSettings;
  userId?: string;
  sequenceId?: string;
  ficheDePrepId?: string;
  ficheDePrepTitle?: string;
  fieldsToDisplay?: string[];
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
}

const fetchFicheDePrepAPI = (s3Url, ficheDePrepTitle) => {
  const fileName = (ficheDePrepTitle || 'fiche_de_prep').replace(/ /g, '_');

  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${fileName}.pdf`);
    });
};

export default async function downloadFicheDePrep(props: Props) {
  const {
    userId,
    sequenceId,
    ficheDePrepId,
    ficheDePrepTitle,
    onStart,
    onFinished,
    onError,
    ...params
  } = props;

  onStart();

  const queryParams = buildFDPPDFParams(params);
  const requestURL = userId
    ? `/users/${userId}/sequences/${sequenceId}/fiches-de-prep/${ficheDePrepId}/generate?${queryParams.toString()}`
    : `/sequences/${sequenceId}/fiches-de-prep/${ficheDePrepId}/generate?${queryParams.toString()}`;
  try {
    const response: PdfResponse = await request(requestURL, { method: 'GET' });
    const token = response.pdf_token;
    await fetchFicheDePrepAPI(token, ficheDePrepTitle);
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadFicheDePrep() {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const fieldsToDisplay = useFieldsToDisplay();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);

  const download = ({
    ficheDePrep,
    ...params
  }: {
    ficheDePrep?: FicheDePrep;
    userId: string;
    settings?: FDPPDFSettings;
  }) =>
    downloadFicheDePrep({
      sequenceId: ficheDePrep?.sequenceId,
      ficheDePrepTitle: ficheDePrep?.title,
      ficheDePrepId: ficheDePrep?.id,
      fieldsToDisplay,
      ...params,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
    });

  return {
    download,
    isDownloading,
  };
}
